/**
 * How the editor finishes a view's print guide before keying (owner, 2026-10-15): Flatten the
 * magenta, and the camera shading fix ("add option to correct the shading clip in editor"). The cloud flattens the print guide to pure magenta and keeps the provider's shading
 * only around the camera; where that region fell short, the island's lip was flattened. Inside
 * each camera island, widened by a margin the operator sets, the guide takes the provider's own
 * pixels back. Openings hold the clear image's pixels, not ink, so they are never touched.
 */
import type { WireView } from "@genz/engine-bind";
import { get2dContext, loadImagePixels, rgbaToPngBytes } from "./pixels.js";

/** Openings closer than 5 % of the long side share one island (the cloud's `ISLAND_GAP`). */
const ISLAND_GAP = 0.05;
/** The width of the blend at the island's edge, so the fix leaves no hard line. */
const FEATHER_PX = 2;
/** `min(R, B) − G` at or above this is magenta ink (the cloud's weak marker score). */
const INK_SCORE = 12;
/** An opening smaller than this share of the image is key noise, not a hole in the case. */
const MIN_OPENING = 0.00005;
/** The share of the guide's ink the original must also ink to be the same picture. */
const MIN_MATCH = 0.9;
/** Magenta however it is lit has red and blue close (the cloud's `MAX_RED_BLUE_GAP`). */
const MAX_RED_BLUE_GAP = 70;
const MAGENTA = [255, 0, 255] as const;
/** The cloud's own margin around an island when it flattens: 3.5 % of the long side. */
export const DEFAULT_ISLAND_MARGIN = 0.035;

/** A rotated rectangle: centre, unit long axis `(ux, uy)` and half extents along it and across. */
export interface Island {
  cx: number;
  cy: number;
  ux: number;
  uy: number;
  hu: number;
  hv: number;
}

type Pt = { x: number; y: number };

function hull(points: Pt[]): Pt[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const half = (list: Pt[]) => {
    const out: Pt[] = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2]!, out[out.length - 1]!, p) <= 0) out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  return sorted.length < 3 ? sorted : [...half(sorted), ...half([...sorted].reverse())];
}

/** The smallest rotated rectangle around `points`: one of its sides lies on a hull edge. */
function minAreaRect(points: Pt[]): Island {
  const h = hull(points);
  let best: Island | null = null;
  let bestArea = Infinity;
  for (let i = 0; i < Math.max(1, h.length); i++) {
    const a = h[i]!;
    const b = h[(i + 1) % h.length]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const [ux, uy] = len > 0 ? [(b.x - a.x) / len, (b.y - a.y) / len] : [1, 0];
    let [minU, maxU, minV, maxV] = [Infinity, -Infinity, Infinity, -Infinity];
    for (const p of h) {
      const u = p.x * ux + p.y * uy;
      const v = -p.x * uy + p.y * ux;
      [minU, maxU, minV, maxV] = [Math.min(minU, u), Math.max(maxU, u), Math.min(minV, v), Math.max(maxV, v)];
    }
    const area = (maxU - minU) * (maxV - minV);
    if (area < bestArea) {
      bestArea = area;
      const [mu, mv] = [(minU + maxU) / 2, (minV + maxV) / 2];
      best = { cx: mu * ux - mv * uy, cy: mu * uy + mv * ux, ux, uy, hu: (maxU - minU) / 2, hv: (maxV - minV) / 2 };
    }
  }
  return best!;
}

function inkScore(data: Uint8ClampedArray, i: number): number {
  return Math.min(data[i]!, data[i + 2]!) - data[i + 1]!;
}

interface Opening {
  points: Pt[];
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * The guide's camera openings: regions without ink that the ink encloses, as the cloud finds them
 * (`_enclosed_openings`). Generate templates rarely carry camera cut-outs, so the image decides.
 * A region touching the frame is the background or an unprinted edge; a speck is key noise.
 */
function openings(rgba: Uint8ClampedArray, width: number, height: number): Opening[] {
  const n = width * height;
  const seen = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (inkScore(rgba, i * 4) >= INK_SCORE) seen[i] = 1;
  const minArea = MIN_OPENING * n;
  const queue = new Int32Array(n);
  const found: Opening[] = [];
  for (let seed = 0; seed < n; seed++) {
    if (seen[seed]) continue;
    seen[seed] = 1;
    let [head, tail, edge] = [0, 0, false];
    queue[tail++] = seed;
    const rowMin = new Map<number, number>();
    const rowMax = new Map<number, number>();
    while (head < tail) {
      const i = queue[head++]!;
      const [x, y] = [i % width, (i / width) | 0];
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) edge = true;
      rowMin.set(y, Math.min(rowMin.get(y) ?? x, x));
      rowMax.set(y, Math.max(rowMax.get(y) ?? x, x));
      for (const j of [x > 0 ? i - 1 : -1, x < width - 1 ? i + 1 : -1, y > 0 ? i - width : -1, y < height - 1 ? i + width : -1]) {
        if (j >= 0 && !seen[j]) {
          seen[j] = 1;
          queue[tail++] = j;
        }
      }
    }
    if (edge || tail < minArea) continue;
    const points: Pt[] = [];
    for (const [y, x] of rowMin) points.push({ x, y }, { x: rowMax.get(y)! + 1, y }, { x, y: y + 1 }, { x: rowMax.get(y)! + 1, y: y + 1 });
    const ys = [...rowMin.keys()];
    found.push({ points, x0: Math.min(...rowMin.values()), x1: Math.max(...rowMax.values()) + 1, y0: Math.min(...ys), y1: Math.max(...ys) + 1 });
  }
  return found;
}

/** One island per group of the guide's openings closer than `ISLAND_GAP`, in pixels. */
export function cameraIslands(rgba: Uint8ClampedArray, width: number, height: number): Island[] {
  const holes = openings(rgba, width, height);
  const gap = ISLAND_GAP * Math.max(width, height);
  const group = holes.map((_, i) => i);
  const root = (i: number): number => (group[i] === i ? i : (group[i] = root(group[i]!)));
  for (let i = 0; i < holes.length; i++) {
    for (let j = i + 1; j < holes.length; j++) {
      const [a, b] = [holes[i]!, holes[j]!];
      const dx = Math.max(0, a.x0 - b.x1, b.x0 - a.x1);
      const dy = Math.max(0, a.y0 - b.y1, b.y0 - a.y1);
      if (Math.hypot(dx, dy) < gap) group[root(i)] = root(j);
    }
  }
  const members = new Map<number, Pt[]>();
  holes.forEach((h, i) => members.set(root(i), [...(members.get(root(i)) ?? []), ...h.points]));
  return [...members.values()].map(minAreaRect);
}

/** Each pixel's weight inside the camera islands widened by `marginPx`: 1 inside, 0 outside,
 *  blended over `FEATHER_PX`. Overlapping islands keep the strongest weight, so no seam. */
function islandWeights(guide: Uint8ClampedArray, width: number, height: number, marginPx: number): Float32Array {
  const weights = new Float32Array(width * height);
  for (const isl of cameraIslands(guide, width, height)) {
    const reach = Math.hypot(isl.hu, isl.hv) + marginPx + FEATHER_PX;
    const [x0, x1] = [Math.max(0, Math.floor(isl.cx - reach)), Math.min(width - 1, Math.ceil(isl.cx + reach))];
    const [y0, y1] = [Math.max(0, Math.floor(isl.cy - reach)), Math.min(height - 1, Math.ceil(isl.cy + reach))];
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const [dx, dy] = [x - isl.cx, y - isl.cy];
        const u = Math.abs(dx * isl.ux + dy * isl.uy) - isl.hu;
        const v = Math.abs(-dx * isl.uy + dy * isl.ux) - isl.hv;
        const weight = Math.min(1, Math.max(0, 0.5 - (Math.max(u, v) - marginPx) / FEATHER_PX));
        const p = y * width + x;
        if (weight > weights[p]!) weights[p] = weight;
      }
    }
  }
  return weights;
}

/**
 * `cleaned` with the provider's own pixels from `original` back inside each camera island widened by
 * `marginPx`, blended over `FEATHER_PX` at the edge. Only pixels that are ink in both images
 * change. Returns a new buffer; both inputs must be `width × height` RGBA.
 */
export function restoreCameraShading(
  cleaned: Uint8ClampedArray,
  original: Uint8ClampedArray,
  width: number,
  height: number,
  marginPx: number,
): Uint8ClampedArray {
  const weights = islandWeights(cleaned, width, height, marginPx);
  const out = new Uint8ClampedArray(cleaned);
  for (let p = 0; p < weights.length; p++) {
    const weight = weights[p]!;
    const i = p * 4;
    if (!weight || inkScore(cleaned, i) < INK_SCORE || inkScore(original, i) < INK_SCORE) continue;
    for (let c = 0; c < 3; c++) out[i + c] = Math.round(cleaned[i + c]! * (1 - weight) + original[i + c]! * weight);
  }
  return out;
}

/**
 * `guide` with its ink painted flat pure magenta outside the camera islands widened by `marginPx`
 * (owner, 2026-10-15: flattening is a choice, per view). The camera lip keeps its shading, as
 * the cloud's flattening does; openings and the clear image are untouched.
 */
export function flattenMagenta(guide: Uint8ClampedArray, width: number, height: number, marginPx: number): Uint8ClampedArray {
  const weights = islandWeights(guide, width, height, marginPx);
  const out = new Uint8ClampedArray(guide);
  for (let p = 0; p < weights.length; p++) {
    const i = p * 4;
    if (inkScore(guide, i) < INK_SCORE || Math.abs(guide[i]! - guide[i + 2]!) > MAX_RED_BLUE_GAP) continue;
    const keep = weights[p]!;
    for (let c = 0; c < 3; c++) out[i + c] = Math.round(guide[i + c]! * keep + MAGENTA[c]! * (1 - keep));
  }
  return out;
}

/** `original` drawn at `width × height`: the provider may return another size of the same frame. */
async function originalAt(url: string, width: number, height: number): Promise<Uint8ClampedArray> {
  const img = await loadImagePixels(url);
  if (img.width === width && img.height === height) return img.data;
  const from = document.createElement("canvas");
  [from.width, from.height] = [img.width, img.height];
  const to = document.createElement("canvas");
  [to.width, to.height] = [width, height];
  const [src, dst] = [get2dContext(from), get2dContext(to)];
  if (!src || !dst) throw new Error("no 2d canvas");
  const frame = src.createImageData(img.width, img.height);
  frame.data.set(img.data);
  src.putImageData(frame, 0, 0);
  dst.drawImage(from, 0, 0, width, height);
  return dst.getImageData(0, 0, width, height).data;
}

/**
 * True when `original` is the picture `cleaned` was made from: it inks nearly all of the guide's
 * ink. A view whose base image was replaced since, or a misaligned original, fails this.
 */
export function sameGuide(cleaned: Uint8ClampedArray, original: Uint8ClampedArray): boolean {
  let [ink, shared] = [0, 0];
  for (let i = 0; i < cleaned.length; i += 4) {
    if (inkScore(cleaned, i) < INK_SCORE) continue;
    ink++;
    if (inkScore(original, i) >= INK_SCORE) shared++;
  }
  return ink > 0 && shared / ink >= MIN_MATCH;
}

/** How a view's print guide is finished before keying: its shading fix and its flattening. */
export type GuideFinish = Pick<WireView, "shading_fix" | "flatten_magenta">;

/** True when `finish` changes the print guide at all. */
export function changesGuide(finish: GuideFinish | null | undefined): boolean {
  return Boolean(finish?.shading_fix || finish?.flatten_magenta);
}

/** The case image's pixels with the view's `finish` applied around its camera islands. */
export async function finishedPixels(caseUrl: string, finish: GuideFinish) {
  const { width, height, data: loaded } = await loadImagePixels(caseUrl);
  let data = loaded;
  const fix = finish.shading_fix;
  const margin = (fix?.margin ?? DEFAULT_ISLAND_MARGIN) * Math.max(width, height);
  if (fix) {
    const original = await originalAt(fix.original_ref, width, height);
    if (!sameGuide(data, original)) {
      throw new Error("The original AI image does not match this view's print guide; remove the camera shading fix.");
    }
    data = restoreCameraShading(data, original, width, height, margin);
  }
  if (finish.flatten_magenta) data = flattenMagenta(data, width, height, margin);
  return { data, width, height };
}

/** {@link finishedPixels} as an object URL the editor loads like any case image. */
export async function finishedUrl(caseUrl: string, finish: GuideFinish): Promise<string> {
  const { data, width, height } = await finishedPixels(caseUrl, finish);
  const png = rgbaToPngBytes(data, width, height);
  if (!png) throw new Error("the corrected case image could not be encoded");
  return URL.createObjectURL(new Blob([png as BlobPart], { type: "image/png" }));
}
